import { Divider } from "primereact/divider";
import { classNames } from "primereact/utils";
import { getChatTimestamp } from "../utils";
import { useContext } from "react";
import { NotificationBadgeContext } from "../NotificationBadgeContext";

/**
 * Barra lateral de chat que muestra la lista de contactos
 * @param {object} props
 * @param {boolean} props.isActive - Indica si la barra lateral está activa
 * @param {Array} props.contacts - Lista de contactos para mostrar en la barra lateral
 * @param {Array} props.supportContacts - Lista de contactos de soporte
 * @param {Function} props.onClickContact
 * @param {Function} props.onClickUser
 * @param {number?} props.selectedId
 * @param {boolean} [props.isLoading=false] - Indica si los contactos están cargando
 * @param {React.RefObject} [props.sidebarRef=null] - Referencia al elemento de la barra lateral
 */
export default function ChatSidebar({
  isActive,
  contacts,
  supportContacts,
  onClickContact,
  onClickUser,
  selectedId,
  isLoading = false,
  sidebarRef = null,
}) {
  const [notifications] = useContext(NotificationBadgeContext);

  return (
    <div
      ref={sidebarRef}
      className={classNames("col-3 overflow-y-auto py-3 surface-50", {
        "flex flex-column": isActive,
        hidden: !isActive,
      })}
      style={{
        fontFamily: "var(--font-family)",
        maxHeight: "calc(100vh - 80px)",
      }}
    >
      <span className="text-lg font-semibold px-3">Atención al usuario</span>
      {isLoading && (
        <div className="text-center p-3">
          <i className="text-2xl pi pi-spin pi-spinner text-color-secondary"></i>
        </div>
      )}
      {!isLoading && supportContacts.length === 0 && (
        <div className="text-center p-3">
          <p className="text-gray-600">
            No hay contactos de soporte disponibles.
          </p>
        </div>
      )}
      <ul className="list-none p-0 m-0">
        {supportContacts.map((contact) => (
          <li
            key={contact.id}
            className={classNames(
              "flex align-items-center gap-2 p-3 hover:bg-gray-100 cursor-pointer",
              {
                "bg-gray-200": selectedId === contact.id,
              }
            )}
            onClick={() => {
              if (contact.chat_id !== null) {
                onClickContact({
                  name: contact.first_name + " " + contact.last_name,
                  id: contact.chat_id,
                });
              } else {
                onClickUser(contact);
              }
            }}
          >
            <img src="/Isologo-fin.png" alt={contact.name} className="w-2rem" />
            <span className="font-semibold">
              {contact.first_name} {contact.last_name}
            </span>
          </li>
        ))}
      </ul>

      <Divider className="px-2" />

      <span className="text-lg font-semibold px-3">Contactos</span>
      {isLoading && (
        <div className="text-center p-3">
          <i className="text-2xl pi pi-spin pi-spinner text-color-secondary"></i>
        </div>
      )}
      {!isLoading && contacts.length === 0 && (
        <div className="text-center p-3">
          <p className="text-gray-600">No has iniciado ninguna conversación.</p>
          <p className="text-gray-600">
            Presiona <b>(+)</b> en la esquina inferior derecha.
          </p>
        </div>
      )}
      <ul className="list-none p-0 m-0">
        {contacts.map((contact) => (
          <li
            key={contact.id}
            className={classNames(
              "flex align-items-start gap-3 p-3 hover:bg-gray-100 cursor-pointer",
              {
                "bg-gray-200": selectedId === contact.id,
              }
            )}
            onClick={() => onClickContact(contact)}
          >
            {contact.picture !== "" ? (
              <img
                src={contact.picture}
                alt={contact.name}
                className="w-3rem border-circle"
              />
            ) : (
              <div
                className="surface-200 border-circle w-3rem flex align-items-center justify-content-center text-gray-600"
                style={{ height: "2.5rem" }}
              >
                <i className="pi pi-user"></i>
              </div>
            )}
            <div className="flex flex-column w-full">
              <span className="font-semibold flex align-items-center gap-2">
                {contact.name}
                {notifications[contact.id] > 0 && (
                  <span
                    className="border-circle bg-cyan-600 text-white text-xs px-2 py-1 ml-2"
                    style={{
                      minWidth: 20,
                      textAlign: "center",
                      display: "inline-block",
                    }}
                  >
                    {notifications[contact.id]}
                  </span>
                )}
              </span>
              <span className="text-sm text-gray-600">{contact.preview}</span>
              <span className="ml-auto text-xs text-gray-500">
                {getChatTimestamp(contact.last_message_timestamp)}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
